import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {ReviewCarousel} from 'components/Reviews/ReviewCarousel'
import {reviewMessages} from 'components/Reviews/ReviewMessages'
import {REVIEWS_PAGE_SIZE} from 'components/Reviews/ReviewsList/ReviewsList'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getLanguageCode} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {useTranslateReviewsQuery} from 'modules/translateReviewsApi'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useGetUserReviewsQuery} from '@vio/core/src/sapi/services/searchApi'

import {SelectedElementType} from './ReviewsTrackClick'

interface Props {
  hotelId?: string
}

export const ReviewCarouselSection: React.FC<Props> = ({hotelId}) => {
  const toggle = useToggle()
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const showOverlay = useShowOverlay()

  const {data, isLoading} = useGetUserReviewsQuery(
    {
      hotelId,
      language: languageCode,
      pageSize: REVIEWS_PAGE_SIZE
    },
    {
      skip: !hotelId
    }
  )

  const handleReadAllReviews = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.ReviewDetails,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.ReviewCard
        }
      })
    )
    showOverlay({
      type: OverlayType.GuestReviews
    })
  }, [dispatch, showOverlay])

  const shouldShowAllReviewsSection = Boolean(data?.reviews?.length)
  const firstSixReviews = data?.reviews?.slice(0, 6)

  // Capped to Hebrew initially
  const {data: translatedReviews, isLoading: isTranslatingReviews} =
    useTranslateReviewsQuery(
      {hotelId, lang: languageCode},
      {
        skip: toggle(
          'feature-translate-reviews',
          true,
          !hotelId || !languageCode
        )
      }
    )

  if (isLoading || isTranslatingReviews) return <SkeletonLoader />

  if (!shouldShowAllReviewsSection) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="s300"
        alignItems="center"
        justifyContent="center"
        paddingTop="s700"
        paddingBottom="s700"
        paddingLeft="s500"
        paddingRight="s500"
      >
        <Text variant="titleM" align="center">
          {formatMessage(reviewMessages.noReviewsTitle)}
        </Text>
        <Text variant="bodyS" align="center">
          {formatMessage(reviewMessages.noReviewsDescription)}
        </Text>
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap="s500">
      <ReviewCarousel
        reviews={firstSixReviews}
        translatedReviews={translatedReviews}
        handleReadMoreClick={handleReadAllReviews}
      />
      <Box paddingLeft="s500" paddingRight="s500">
        <Button
          variant="tertiary"
          fullWidth
          size="xl"
          onClick={handleReadAllReviews}
        >
          {formatMessage(reviewMessages.readAllReviews)}
        </Button>
      </Box>
    </Box>
  )
}
